// Phase 0084 — Role Responsibility Master list page.
// Shows all role records with approval-status filter + quick navigation
// to the detail editor.

import { useState } from 'react'
import { App, Button, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'

import { roleMasterApi, type RoleMaster } from '@/features/hr/roleMasterApi'
import { hrAutomationApi } from '@/features/hr/schedulerApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  OBSOLETE: 'default',
  DETAILS_PENDING: 'blue',
}

const CRITICALITY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'volcano',
  MEDIUM: 'gold',
  LOW: 'green',
}

export default function RoleMasterListPage() {
  const navigate = useNavigate()
  const { message: msg } = App.useApp()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['role-master-list', statusFilter],
    queryFn: () => roleMasterApi.listRoles(true, statusFilter),
  })

  const generateTemplatesMut = useMutation({
    mutationFn: () => hrAutomationApi.generateTemplatesFromResponsibilities(),
    onSuccess: (data) => {
      msg.success(
        `Generated ${data.templates_created} templates ` +
        `(${data.skipped_existing} already had one, ` +
        `${data.skipped_unmapped_frequency} skipped frequency).`)
    },
    onError: (e: Error) => msg.error(e.message),
  })

  const filtered = rows.filter((r) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      r.role_code.toLowerCase().includes(q) ||
      r.designation_name.toLowerCase().includes(q)
    )
  })

  const columns: ColumnsType<RoleMaster> = [
    {
      title: 'Role Code',
      dataIndex: 'role_code',
      key: 'role_code',
      width: 200,
      render: (code: string) => <Text code>{code}</Text>,
      sorter: (a, b) => a.role_code.localeCompare(b.role_code),
    },
    {
      title: 'Designation',
      dataIndex: 'designation_name',
      key: 'designation_name',
      width: 240,
    },
    {
      title: 'Level',
      dataIndex: 'role_level',
      key: 'role_level',
      width: 140,
      render: (l: string | null) => l || '—',
    },
    {
      title: 'Shift',
      dataIndex: 'shift_type',
      key: 'shift_type',
      width: 100,
      render: (s: string | null) => s || '—',
    },
    {
      title: 'Criticality',
      dataIndex: 'criticality',
      key: 'criticality',
      width: 110,
      render: (c: string) => <Tag color={CRITICALITY_COLORS[c]}>{c}</Tag>,
      filters: [
        { text: 'CRITICAL', value: 'CRITICAL' },
        { text: 'HIGH', value: 'HIGH' },
        { text: 'MEDIUM', value: 'MEDIUM' },
        { text: 'LOW', value: 'LOW' },
      ],
      onFilter: (v, r) => r.criticality === v,
    },
    {
      title: 'Flags',
      key: 'flags',
      width: 200,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.statutory_role_flag && <Tag color="purple">Statutory</Tag>}
          {r.safety_critical_flag && <Tag color="red">Safety</Tag>}
          {r.production_critical_flag && <Tag color="orange">Prod-Critical</Tag>}
          {r.requires_certification && <Tag color="blue">Cert</Tag>}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 160,
      render: (s: string) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Version',
      dataIndex: 'current_version_no',
      key: 'current_version_no',
      width: 90,
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, r) => (
        <Button size="small" onClick={() => navigate(`/hr/role-master/${r.role_code}`)}>
          Open
        </Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Role Responsibility Master"
        subtitle="Edit role responsibilities, KPIs, authorities, skills, training, compliance and reporting"
      />
      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          placeholder="Search by role code or name"
          allowClear
          style={{ width: 320 }}
          onSearch={setSearch}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          placeholder="Approval status"
          style={{ width: 200 }}
          onChange={(v) => setStatusFilter(v)}
          options={[
            { label: 'DRAFT', value: 'DRAFT' },
            { label: 'DETAILS_PENDING', value: 'DETAILS_PENDING' },
            { label: 'PENDING_APPROVAL', value: 'PENDING_APPROVAL' },
            { label: 'APPROVED', value: 'APPROVED' },
            { label: 'REJECTED', value: 'REJECTED' },
            { label: 'OBSOLETE', value: 'OBSOLETE' },
          ]}
        />
        <Button onClick={() => { refetch(); msg.info('Refreshed') }}>Refresh</Button>
        <Button type="primary" onClick={() => navigate('/hr/role-master/new')}>
          Add Role
        </Button>
        <Button onClick={() => navigate('/hr/org-chart')}>Org Chart</Button>
        <Button onClick={() => {
          Modal.confirm({
            title: 'Generate task templates from responsibility lines?',
            content:
              'Creates one ResponsibilityTaskTemplate per active responsibility line on '
              + 'APPROVED roles that does not already have a template. Idempotent — '
              + 're-running skips lines that already have a template. '
              + 'HR can edit the generated templates later.',
            onOk: () => generateTemplatesMut.mutateAsync(),
          })
        }} loading={generateTemplatesMut.isPending}>
          Generate Task Templates
        </Button>
        <Text type="secondary">
          {filtered.length} of {rows.length} roles
        </Text>
      </Space>

      <Table<RoleMaster>
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1300 }}
      />
    </>
  )
}
